import { useState } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { showToast } from '../utils/toast';

const C = { cream:'#fdf8f0', cream2:'#f5f0e4', blue:'#1562c8', gold:'#f0bf6b', brown:'#55381f', dark:'#2a2118', white:'#ffffff', green:'#1a8a4a' };
const BORDER = `2px solid ${C.dark}`;
const SHADOW = `3px 3px 0 ${C.dark}`;

const STARS = [1, 2, 3, 4, 5];
const LABELS = { 1: 'Not great', 2: 'Needs work', 3: 'It\'s okay', 4: 'Pretty good', 5: 'Love it!' };

const isComposer = (profile) =>
  profile?.account_type === 'composer' || profile?.account_type === 'admin';

function Star({ filled, size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? C.gold : 'none'} stroke={filled ? C.gold : 'rgba(42,33,24,0.25)'} strokeWidth="1.5" style={{ display: 'block' }}>
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  );
}

export function FeedbackModal({ isOpen, onClose, userProfile }) {
  const [rating, setRating]         = useState(0);
  const [hovered, setHovered]       = useState(0);
  const [message, setMessage]       = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]             = useState(false);

  if (!isOpen) return null;

  const composer = isComposer(userProfile);
  const question = composer
    ? 'How is your experience with SongAnalyzer so far? Is there anything you\'d like us to improve?'
    : 'How\'s your experience with CodaVault so far?';
  const placeholder = composer
    ? 'E.g. the AI tagging, split sheet exports, genre detection…'
    : 'Anything specific you\'d like to share? (optional)';

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from('user_feedback').insert({
        user_id: userProfile.id,
        rating,
        message: message.trim() || null,
      });
      if (error) throw error;
      localStorage.setItem('cv_feedback_last', Date.now().toString());
      setDone(true);
    } catch (err) {
      showToast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!done) localStorage.setItem('cv_feedback_dismissed', Date.now().toString());
    setRating(0); setMessage(''); setDone(false);
    onClose();
  };

  const active = hovered || rating;

  return (
    <div
      onClick={e => e.target === e.currentTarget && handleClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(42,33,24,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div style={{ background: C.cream, border: BORDER, boxShadow: `6px 6px 0 ${C.dark}`, borderRadius: 16, width: '100%', maxWidth: 420, overflow: 'hidden' }}>
        <div style={{ background: C.blue, padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: "'GeistPixel', monospace", fontSize: 13, color: C.white }}>Share Your Feedback</span>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center' }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginBottom: 14 }}>
                {STARS.map(s => <Star key={s} filled={s <= rating} size={24} />)}
              </div>
              <div style={{ fontFamily: "'GeistPixel', monospace", fontSize: 15, color: C.dark, marginBottom: 8 }}>Thank you!</div>
              <div style={{ fontSize: 13, color: C.brown, marginBottom: 20, lineHeight: 1.6 }}>Your feedback helps us make CodaVault better for everyone.</div>
              <button onClick={handleClose} style={{ background: C.blue, border: BORDER, boxShadow: SHADOW, borderRadius: 8, padding: '10px 24px', color: C.white, fontFamily: "'GeistPixel', monospace", fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, color: C.brown, marginBottom: 20, lineHeight: 1.6, marginTop: 0 }}>
                {question}
              </p>

              {/* Star rating */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, justifyContent: 'center' }}>
                {STARS.map(s => (
                  <button
                    key={s}
                    onClick={() => setRating(s)}
                    onMouseEnter={() => setHovered(s)}
                    onMouseLeave={() => setHovered(0)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, transition: 'transform 0.1s', transform: active >= s ? 'scale(1.18)' : 'scale(1)' }}
                  >
                    <Star filled={active >= s} size={30} />
                  </button>
                ))}
              </div>
              <div style={{ textAlign: 'center', fontSize: 12, color: C.blue, fontWeight: 700, minHeight: 18, marginBottom: 18, fontFamily: "'GeistPixel', monospace" }}>
                {active ? LABELS[active] : ''}
              </div>

              {/* Message */}
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={placeholder}
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', background: C.cream2, border: `1px solid rgba(42,33,24,0.2)`, borderRadius: 8, padding: '10px 12px', color: C.dark, fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: "'Roboto', sans-serif", lineHeight: 1.5 }}
              />

              <button
                onClick={handleSubmit}
                disabled={!rating || submitting}
                style={{ width: '100%', marginTop: 14, padding: '12px', background: rating ? C.blue : C.cream2, color: rating ? C.white : C.brown, border: BORDER, boxShadow: rating ? SHADOW : 'none', borderRadius: 8, fontFamily: "'GeistPixel', monospace", fontSize: 13, fontWeight: 700, cursor: rating ? 'pointer' : 'not-allowed', opacity: submitting ? 0.7 : 1, transition: 'all 0.1s' }}
              >
                {submitting ? 'Sending…' : 'Send Feedback'}
              </button>

              <button onClick={handleClose} style={{ width: '100%', marginTop: 8, padding: '8px', background: 'none', border: 'none', color: C.brown, fontSize: 12, cursor: 'pointer' }}>
                Maybe later
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
